import React from 'react'
import type { CandleInterval } from '@nutrimatic/sdk'

import { useTranslation } from '@/features/locale/LocaleProvider'
import { NUTRIMATIC_CHART_INTERVALS } from '@/features/market/useNutrimaticMarketHub'
import { nutrimaticDevice } from '@/nutrimatic'
import { cn } from '@/lib/utils'

type Props = {
  value: CandleInterval
  onChange: (interval: CandleInterval) => void
  variant?: 'default' | 'nutrimatic' | 'console'
  className?: string
}

/** Real SDK candle intervals (replaces fake 1h/24h/7d/30d client slices). */
export function NutrimaticIntervalSelector({
  value,
  onChange,
  variant = 'default',
  className,
}: Props) {
  const t = useTranslation()

  if (variant === 'nutrimatic') {
    return (
      <div
        className={cn('nutrimatic-physical-selector', className)}
        role="group"
        aria-label={t.common.chartTimeframe}
      >
        {NUTRIMATIC_CHART_INTERVALS.map((iv) => (
          <button
            key={iv}
            type="button"
            onClick={() => onChange(iv)}
            aria-pressed={value === iv}
            className="nutrimatic-physical-selector__btn"
          >
            {iv}
          </button>
        ))}
      </div>
    )
  }

  if (variant === 'console') {
    return (
      <div
        className={cn(nutrimaticDevice.readoutDisplayTimeframe, className)}
        role="group"
        aria-label={t.common.chartTimeframe}
      >
        {NUTRIMATIC_CHART_INTERVALS.map((iv) => (
          <button
            key={iv}
            type="button"
            onClick={() => onChange(iv)}
            aria-pressed={value === iv}
            className={nutrimaticDevice.readoutDisplayTimeframeBtn}
          >
            {iv}
          </button>
        ))}
      </div>
    )
  }

  return (
    <div
      className={cn('surface-inset flex rounded-lg p-0.5 text-[10px]', className)}
      role="group"
      aria-label={t.common.chartTimeframe}
    >
      {NUTRIMATIC_CHART_INTERVALS.map((iv) => (
        <button
          key={iv}
          type="button"
          onClick={() => onChange(iv)}
          className={cn(
            'rounded-md px-2 py-0.5 transition-colors',
            value === iv
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          {iv}
        </button>
      ))}
    </div>
  )
}
